import React from 'react';
import { ERouteControlViewType, Map as NewbuildingMapComponent } from '@cian/frontend-newbuilding-map-component';
import {
  EMapService,
  TInfrastructureItem,
  TRegularInfrastructureItem,
  TTAInfrastructureItem,
} from '../../../types/map';
import { trackBuildRouteClick, trackBuildRouteSuccess } from '../analytics';
import {
  checkIsInfrastructureRoute,
  getCurrentRouteItem,
  isTransportAccessibilityInfrastructureItem,
} from '../helpers';
import { IRoutingBuildSuccessParams } from '../../../components/Map';
import { getTelemetry } from '@cian/telemetry/browser';
import { YANDEX_API_TELEMETRY_KEY } from '../../../constants/map';
import { useTransportAccessibilityRouting } from './useTransportAccessibilityRouting';
import { useSelector } from 'react-redux';
import { getIsMapServicesVisible, getIsTransportAccessibilityServiceAvailable } from '../../../selectors/mapServices';
import { getGeoCoordinates, getNewbuildingDisplayName } from '../../../selectors/newbuilding';

interface IUseRoutingParams {
  hasFutureInfrastructure: boolean;
  mapApi: NewbuildingMapComponent | null;
  regularInfrastructureType: string | undefined;
  setMapService: (mapService: EMapService) => void;
}

export function useRouting({
  hasFutureInfrastructure,
  mapApi,
  regularInfrastructureType,
  setMapService,
}: IUseRoutingParams) {
  const { setTARouting, resetTARouting } = useTransportAccessibilityRouting(mapApi);

  const currentRouteItemRef = React.useRef<TInfrastructureItem | null>(null);

  const pinCoordinates = useSelector(getGeoCoordinates);
  const newbuildingName = useSelector(getNewbuildingDisplayName);

  const isMapServicesVisible = useSelector(getIsMapServicesVisible);
  const isTransportAccessibilityServiceAvailable = useSelector(getIsTransportAccessibilityServiceAvailable);

  const setRoutingStart = React.useCallback(
    (geoItem?: TRegularInfrastructureItem, routeControlViewType?: ERouteControlViewType) => {
      if (mapApi) {
        const from = pinCoordinates ? { coordinates: pinCoordinates, fixed: true, label: newbuildingName } : undefined;
        const to = geoItem ? { coordinates: geoItem.coordinates } : undefined;

        mapApi.startRouting(
          from,
          to,
          'properties.name === "main" && geometry.type === "Polygon"',
          undefined,
          routeControlViewType,
        );
      }
    },
    [pinCoordinates, mapApi, newbuildingName],
  );

  const handleBalloonRouteButtonClick = React.useCallback(
    (item: TInfrastructureItem, routeControlViewType: ERouteControlViewType) => {
      trackBuildRouteClick(item.type || regularInfrastructureType);
      currentRouteItemRef.current = item;

      if (isTransportAccessibilityInfrastructureItem(item)) {
        setTARouting(item as TTAInfrastructureItem);
      } else {
        setRoutingStart(item as TRegularInfrastructureItem, routeControlViewType);

        if (isMapServicesVisible) {
          setMapService(EMapService.Routes);
        }
      }
    },
    [isMapServicesVisible, regularInfrastructureType, setMapService, setRoutingStart],
  );

  const handleRoutingClick = React.useCallback(
    (routeControlViewType?: ERouteControlViewType) => {
      setRoutingStart(undefined, routeControlViewType);

      // TODO: Оставить одно условие, когда решено будет оставить редизайн в NB-1172
      trackBuildRouteClick(isMapServicesVisible ? undefined : regularInfrastructureType);
    },
    [setRoutingStart, regularInfrastructureType, isMapServicesVisible],
  );

  const handleRoutingBuildInit = React.useCallback(() => {
    getTelemetry().increment(YANDEX_API_TELEMETRY_KEY, 2);
  }, []);

  const handleRouteBuildingSuccess = React.useCallback(
    (params: IRoutingBuildSuccessParams) => {
      if (params.routes.length) {
        const infrastructurePoint = currentRouteItemRef.current;

        const isObjectPoint = !!infrastructurePoint && checkIsInfrastructureRoute(infrastructurePoint, params);
        const isFutureObject = infrastructurePoint ? 'constructionYearFrom' in infrastructurePoint : false;

        currentRouteItemRef.current = getCurrentRouteItem(currentRouteItemRef.current, isObjectPoint);

        trackBuildRouteSuccess({
          searchResult: params,
          infrastructureType: infrastructurePoint?.type || regularInfrastructureType,
          isObjectPoint,
          isFutureObject,
          hasFutureInfrastructure,
          // TODO: После выпила фича-флага в NB-1172 достаточно использовать только isTransportAccessibilityServiceAvailable.
          hasTransportAccessibilityService: isMapServicesVisible && isTransportAccessibilityServiceAvailable,
        });
      }
    },
    [
      hasFutureInfrastructure,
      isMapServicesVisible,
      isTransportAccessibilityServiceAvailable,
      regularInfrastructureType,
    ],
  );

  return {
    handleBalloonRouteButtonClick,
    handleRoutingClick,

    handleRoutingBuildInit,
    handleRouteBuildingSuccess,

    resetTARouting,
  };
}
